/**
 * CostSchemeBoard — the timeline ("osa") view of the Cost Scheme agenda.
 *
 * Lays out a CSS grid: a sticky ruler gutter + one column per day, with two sticky horizontal bands on top
 * (accommodation + services strips) and the per-day hour timelines below. Owns purely-visual board state
 * (which days are collapsed, whether the night window is expanded, and which drag is in progress) and wires
 * every child to the board model mutations from {@link useCostSchemeBoard}. The draggable placeholder
 * palette sits to the right.
 */
import { useMemo, useState } from 'react';
import { Bed, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PlaceholderType } from '@/api/generated/schemas';
import { cn } from '@/shared/lib/utils';
import {
  DAY_END,
  DEFAULT_START,
  NIGHT_START,
  TypeIcon,
  computeDayFactors,
  computeDayLayout,
  gridTemplateColumns,
  type BoardPart,
  type PalettePayload,
} from '../model';
import type { CostSchemeBoard as Board } from '../useCostSchemeBoard';
import { CostSchemePalette } from '../palette';
import { TimeRuler } from './TimeRuler';
import { DayColumn } from './DayColumn';
import { AccommodationStrip } from './AccommodationStrip';
import { ServicesStrip } from './ServicesStrip';

type StyleVars = React.CSSProperties & Record<string, string | number>;

interface CostSchemeBoardProps {
  board: Board;
  readOnly: boolean;
  onOpenPart: (uid: string) => void;
  selectedUid: string | null;
}

export function CostSchemeBoard({
  board,
  readOnly,
  onOpenPart,
  selectedUid,
}: CostSchemeBoardProps) {
  const { t } = useTranslation('venua-planner');
  const { model } = board;
  const dayCount = model.lengthDays;
  // Nulanoční program (jeden den) nemá kde ubytovat — pruh ubytování ani placeholdery ubytování
  // se pak vůbec nenabízejí.
  const hasNights = dayCount > 1;

  const [collapsedDays, setCollapsedDays] = useState<ReadonlySet<number>>(new Set());
  const [nightExpanded, setNightExpanded] = useState(false);
  const [actDragging, setActDragging] = useState(false);
  const [accomDragging, setAccomDragging] = useState(false);
  const [svcDragging, setSvcDragging] = useState(false);
  const [palettePayload, setPalettePayload] = useState<PalettePayload | null>(null);
  const [dragHover, setDragHover] = useState<{ dayIdx: number; y: number } | null>(null);

  const dayStart = nightExpanded ? NIGHT_START : DEFAULT_START;
  const dayHours = DAY_END - dayStart;

  // Pack each day's overlapping timed parts into lanes; lane counts drive the per-day column width factors,
  // which the grid template and both strips share so everything stays column-aligned.
  const timedByDay = useMemo(() => {
    const buckets: BoardPart[][] = Array.from({ length: dayCount }, () => []);
    model.parts.forEach((p) => {
      if (p.kind === 'timed' && p.day >= 0 && p.day < dayCount) buckets[p.day].push(p);
    });
    return buckets.map((parts) => computeDayLayout(parts));
  }, [model.parts, dayCount]);

  const laneCounts = useMemo(() => timedByDay.map((d) => d.laneCount), [timedByDay]);
  const dayFactors = useMemo(
    () => computeDayFactors(laneCounts, collapsedDays),
    [laneCounts, collapsedDays]
  );
  const gridColumns = useMemo(() => gridTemplateColumns(dayFactors), [dayFactors]);

  // A palette drag knows its placeholder type, so drop zones can be typed: accommodation placeholders may
  // land only in the accommodation strip, everything else only on the timeline / services strip.
  const paletteDragging = palettePayload !== null;
  const paletteIsAccom = palettePayload?.type === PlaceholderType.Accommodation;
  const anyDragging = actDragging || accomDragging || svcDragging || paletteDragging;
  const accomDropActive = accomDragging || (paletteDragging && paletteIsAccom);
  const serviceDropActive = svcDragging || (paletteDragging && !paletteIsAccom);
  const timedDropActive = paletteDragging && !paletteIsAccom;

  const toggleDayCollapse = (idx: number) => {
    setCollapsedDays((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const accommodation = model.parts.filter((p) => p.kind === 'accommodation');
  const services = model.parts.filter((p) => p.kind === 'service');

  const rootStyle: StyleVars = {
    '--tp-day-count': dayCount,
    '--tp-night-count': Math.max(1, dayCount - 1),
  };

  return (
    <div className={cn('tp-main', anyDragging && 'tp-main--dragging')} style={rootStyle}>
      <div className="tp-board">
        <div className="tp-grid" style={{ ...rootStyle, gridTemplateColumns: gridColumns }}>
          <div className="tp-grid-corner" />

          {Array.from({ length: dayCount }, (_, i) => {
            const isCollapsed = collapsedDays.has(i);
            return (
              <div key={i} className={cn('tp-day-head', isCollapsed && 'tp-day-head--collapsed')}>
                <button
                  type="button"
                  className="tp-day-collapse"
                  onClick={() => toggleDayCollapse(i)}
                  title={isCollapsed ? t('costScheme.expandDay') : t('costScheme.collapseDay')}
                  aria-label={isCollapsed ? t('costScheme.expandDay') : t('costScheme.collapseDay')}
                >
                  {isCollapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
                </button>
                <div className="tp-day-dow">{t('costScheme.dayShort')}</div>
                <div className="tp-day-date">{i + 1}</div>
                {isCollapsed ? null : (
                  <div className="tp-day-name">
                    {t('costScheme.day')} {i + 1}
                  </div>
                )}
              </div>
            );
          })}

          {hasNights ? (
            <>
              <div className="tp-accom-corner" title={t('costScheme.accommodationStrip')}>
                <Bed size={18} />
              </div>
              <AccommodationStrip
                t={t}
                dayCount={dayCount}
                weights={dayFactors}
                bookings={accommodation}
                readOnly={readOnly}
                dropActive={accomDropActive}
                onOpen={onOpenPart}
                onDropPalette={board.dropAccommodation}
                onMove={board.moveAccommodation}
                onResize={board.resizeAccommodation}
                onDragChange={setAccomDragging}
              />
            </>
          ) : null}

          <div className="tp-svc-corner" title={t('costScheme.servicesStrip')}>
            <TypeIcon type={PlaceholderType.OtherServices} size={18} />
          </div>
          <ServicesStrip
            t={t}
            dayCount={dayCount}
            weights={dayFactors}
            services={services}
            readOnly={readOnly}
            dropActive={serviceDropActive}
            onOpen={onOpenPart}
            onDropPalette={board.dropService}
            onMove={board.moveService}
            onResize={board.resizeService}
            onDragChange={setSvcDragging}
          />

          <TimeRuler
            t={t}
            dayStart={dayStart}
            dayHours={dayHours}
            nightExpanded={nightExpanded}
            onToggleNight={() => setNightExpanded((v) => !v)}
          />

          {Array.from({ length: dayCount }, (_, i) => (
            <DayColumn
              key={i}
              dayIdx={i}
              dayStart={dayStart}
              dayHours={dayHours}
              layout={timedByDay[i]}
              collapsed={collapsedDays.has(i)}
              readOnly={readOnly}
              paletteDropActive={timedDropActive}
              selectedUid={selectedUid}
              dragHover={dragHover}
              setDragHover={setDragHover}
              onDropPalette={board.dropPalette}
              onMove={board.moveTimed}
              onResize={board.resizeTimed}
              onOpen={onOpenPart}
              onExpandNight={() => setNightExpanded(true)}
              onActDragChange={setActDragging}
            />
          ))}
        </div>
      </div>

      <CostSchemePalette
        readOnly={readOnly}
        allowAccommodation={hasNights}
        onItemDragStart={setPalettePayload}
        onItemDragEnd={() => setPalettePayload(null)}
      />
    </div>
  );
}
